// THE OPERATOR CONSOLE'S SIGN-IN-TEXTS FORM, as decisions over values (owner's decision B1, 4.11.2026,
// task dfbe33eb). Four fields — iskulause fi/en and the line under it, fi/en — each of which is either
// the operator's own words or, when left empty, the translation file's shipped default.
//
// WHY THIS IS A MODULE AND NOT A `switch` INSIDE THE PAGE, which is the arrangement `tenantDeletion.ts`
// and `tenantRoleHolders.ts` already use in this directory: every function below turns a form value or a
// wire value into an i18n KEY or a yes/no, and the interesting cases are the ones nobody clicks through —
// a text one character over the limit, a pasted line break, a 422 for a field the console thought it had
// already refused. Under `fallbackLng: "fi"` a wrong key on any of those renders as Finnish and nothing
// says so.
//
// IT IMPORTS NOTHING AT RUNTIME, deliberately, like its three neighbours: that is what lets Node's own
// test runner load it under `--experimental-strip-types` (`sign-in-texts.test.mjs`). The HTTP calls are
// `./signInTextsApi` and the rendering is `../pages/PlatformSignInTextsPage.tsx`.

/**
 * How long one text may be. The SAME number as the backend's `PlatformSignInTexts.TextMaxLength` and the
 * `varchar(200)` column behind it, spelled here a second time for the zero-runtime-import reason above —
 * and pinned against that C# constant by `sign-in-texts.test.mjs`, so a widened column fails a test here
 * instead of leaving the form refusing what the platform would have accepted.
 */
export const SIGN_IN_TEXT_MAX_LENGTH = 200;

/** The four texts, by the wire name each one carries in both directions. */
export type SignInTextField = "taglineFi" | "taglineEn" | "taglineSubFi" | "taglineSubEn";

/** The texts in force, as `GET /api/auth/sign-in-texts` answers them. A null is "nothing set". */
export type SignInTexts = Record<SignInTextField, string | null>;

/** What the four inputs hold. A blank one means "clear this text", which is what restores the default. */
export type SignInTextValues = Record<SignInTextField, string>;

/** One field of the form, and everything the page needs in order to draw it. */
export interface SignInTextFieldSpec {
  /** The wire name, and the form's own key for the field. */
  field: SignInTextField;
  /**
   * The backend's AUDITED PARAMETER NAME for this text (`PlatformSignInTextRules.TaglineFiParameter` and
   * its three siblings). It is what a 422's `detail` begins with, which is the only reason the console
   * knows it: a refusal naming `tagline_sub_en` has to land on the right input.
   */
  parameter: string;
  /** Which language's sign-in page this text is shown on — NOT the console's own interface language. */
  language: "fi" | "en";
  /**
   * The translation key of the text this field overrides, read in `language` (`t(key, { lng })`) so the
   * Finnish field shows the Finnish default and the English field the English one, whichever language
   * the operator is running the console in. Shown as the input's placeholder: an empty field means that
   * text is what the sign-in page draws, so showing it IS the answer to "what happens if I leave this
   * blank".
   */
  defaultKey: "auth.tagline" | "auth.taglineSub";
  /** The input's own label. */
  labelKey: string;
}

/**
 * The form, in the order it is drawn: both halves of the iskulause, then both halves of the line under it.
 *
 * BY TEXT AND THEN BY LANGUAGE, rather than Finnish pair followed by English pair. The two things an
 * operator compares while writing these are the same sentence in the two languages — they are translations
 * of each other — and the sign-in page shows exactly one of each pair, never a Finnish tagline above an
 * English sub-line.
 */
export const SIGN_IN_TEXT_FIELDS: readonly SignInTextFieldSpec[] = [
  {
    field: "taglineFi",
    parameter: "tagline_fi",
    language: "fi",
    defaultKey: "auth.tagline",
    labelKey: "platformSignInTexts.taglineFi",
  },
  {
    field: "taglineEn",
    parameter: "tagline_en",
    language: "en",
    defaultKey: "auth.tagline",
    labelKey: "platformSignInTexts.taglineEn",
  },
  {
    field: "taglineSubFi",
    parameter: "tagline_sub_fi",
    language: "fi",
    defaultKey: "auth.taglineSub",
    labelKey: "platformSignInTexts.taglineSubFi",
  },
  {
    field: "taglineSubEn",
    parameter: "tagline_sub_en",
    language: "en",
    defaultKey: "auth.taglineSub",
    labelKey: "platformSignInTexts.taglineSubEn",
  },
];

/** Four empty inputs — the form before anything has been read, and after "restore the defaults". */
export const EMPTY_VALUES: SignInTextValues = {
  taglineFi: "",
  taglineEn: "",
  taglineSubFi: "",
  taglineSubEn: "",
};

/** The texts in force as the form holds them: a null becomes an empty input, which means the same thing. */
export function toFormValues(texts: SignInTexts): SignInTextValues {
  return {
    taglineFi: texts.taglineFi ?? "",
    taglineEn: texts.taglineEn ?? "",
    taglineSubFi: texts.taglineSubFi ?? "",
    taglineSubEn: texts.taglineSubEn ?? "",
  };
}

/**
 * The request body for what the form holds.
 *
 * ALL FOUR FIELDS, ALWAYS, including the blank ones as explicit nulls. The route is a REPLACE and not a
 * patch (`SavePlatformSignInTextsRequest`), so an omitted field clears anyway — but sending what the form
 * shows makes the request readable on its own, and makes "restore the defaults" the ordinary submit of an
 * emptied form rather than a different call.
 */
export function toSaveRequest(values: SignInTextValues): Record<SignInTextField, string | null> {
  const body = {} as Record<SignInTextField, string | null>;
  for (const { field } of SIGN_IN_TEXT_FIELDS) {
    const trimmed = values[field].trim();
    body[field] = trimmed === "" ? null : trimmed;
  }
  return body;
}

/** Does the form hold anything other than what is already in force? */
export function isDirty(values: SignInTextValues, texts: SignInTexts): boolean {
  const body = toSaveRequest(values);
  return SIGN_IN_TEXT_FIELDS.some(({ field }) => body[field] !== (texts[field] ?? null));
}

/**
 * Is any text set at all? What "restore the defaults" is offered on: with four empty inputs it would
 * submit the state already in force, which is a button that cannot do anything.
 */
export function hasOverrides(values: SignInTextValues): boolean {
  return SIGN_IN_TEXT_FIELDS.some(({ field }) => values[field].trim() !== "");
}

/**
 * Why this text may not be saved, as an i18n key — or null when it may.
 *
 * THE SAME TWO RULES AS `PlatformSignInTextRules.Refuse`, checked here as well as there, and the
 * duplication is the point rather than an oversight: the server's refusal is one English sentence naming a
 * column (`tagline_sub_en must be plain text: …`), which is the right answer for an API and the wrong one
 * to put in front of an operator. Checking here is what lets the form say which field and which rule, in
 * the operator's own language, beside the input. The server still decides — nothing below is trusted by
 * it — and `signInTextsErrorKey` renders its verdict if the two ever disagree.
 *
 * Measured on the TRIMMED value, exactly as the backend measures it, so a text that is 200 characters
 * plus a trailing space is accepted by both rather than refused here and stored there.
 */
export function refuseSignInText(value: string): string | null {
  const text = value.trim();
  if (text === "") return null;
  if (text.length > SIGN_IN_TEXT_MAX_LENGTH) return "platformSignInTexts.errorTooLong";
  if (!isPlainText(text)) return "platformSignInTexts.errorPlainText";
  return null;
}

/**
 * The backend's `char.IsControl(c) || c is '<' or '>'`, character by character.
 *
 * A LOOP AND NOT A REGEXP, for one reason worth writing down: a character class spelling out the control
 * ranges is what `no-control-regex` exists to stop, and the only way past that rule is a suppression
 * comment — which reads as "this rule does not apply here" when the truth is the opposite. The ranges are
 * .NET's `UnicodeCategory.Control`: C0 (U+0000–U+001F) and C1 (U+007F–U+009F).
 */
function isPlainText(text: string): boolean {
  for (const char of text) {
    const code = char.codePointAt(0) ?? 0;
    if (code <= 0x1f || (code >= 0x7f && code <= 0x9f)) return false;
    if (char === "<" || char === ">") return false;
  }
  return true;
}

/** Every field the form would refuse, in the order they are drawn. Empty when the form may be submitted. */
export function refusedFields(values: SignInTextValues): { field: SignInTextField; key: string }[] {
  return SIGN_IN_TEXT_FIELDS.flatMap(({ field }) => {
    const key = refuseSignInText(values[field]);
    return key ? [{ field, key }] : [];
  });
}

/**
 * Which SENTENCE a failed save gets, and which input it belongs beside.
 *
 * `field` is non-null only for a 422, whose `detail` the backend deliberately begins with the parameter
 * name so that "a form with four inputs cannot act on 'the request is invalid'" stops being true. A 422
 * reaching the console at all means `refuseSignInText` and the server disagreed about one of these texts;
 * the server wins, and the message says so rather than pretending the save was a network problem.
 *
 * Everything else is the page's banner: 403 is a session that is no longer a platform operator's (the
 * route's own gate, ADR-0014 §6 phase 2), and anything else — a 500, a 503, no response at all — leaves
 * the OUTCOME OPEN rather than reporting a failed save.
 *
 * WHY IT MAY NOT SAY "NOTHING WAS SAVED", which is what this key said until the review of task dfbe33eb
 * caught it. `PlatformSignInTextsService.SaveAsync` commits its transaction BEFORE the endpoint builds the
 * response (`tx.CommitAsync` and then the result), so an answer that was lost or timed out is an answer to
 * a write that already landed: the new texts are on every customer's sign-in page while the console claims
 * the previous ones still are. The two claims this console CAN make are that the outcome is unknown and
 * that the retry is safe — the route is a REPLACE of all four texts (`toSaveRequest` always sends four
 * values), so sending the same body again settles the state either way.
 *
 * The 422 and the 403 are the two endings that DO know: both refuse before any write, the 422 inside
 * `PlatformSignInTextRules.Decide` and the 403 at the route's gate.
 */
export function signInTextsErrorKey(
  status: number | null,
  detail: string | null | undefined,
): { key: string; field: SignInTextField | null } {
  if (status === 422) {
    const named = SIGN_IN_TEXT_FIELDS.find(
      ({ parameter }) => typeof detail === "string" && detail.trimStart().startsWith(parameter),
    );
    return { key: "platformSignInTexts.error422", field: named?.field ?? null };
  }
  if (status === 403) return { key: "platformSignInTexts.error403", field: null };
  return { key: "platformSignInTexts.errorUnknown", field: null };
}
